import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
} from "@mui/material";

import { Controller, useForm, useWatch } from "react-hook-form";
import { useSuperAdminPlans } from "../../../context/SuperAdmin/PlanContext";
import {
  PlansTypes,
  PostPlanFields,
} from "../../../interfaces/SuperAdmin/Pages/iDataPlan";
import { useEffect, useState } from "react";
import { environment } from "../../../config";

const CreatePlanModal = () => {
  const {
    isCreateModalOpen,
    toggleCreateModal,
    isCreating,
    tiposPlan,
    isLoadingTipoPlans,
    orgs,
    planCreate,
  } = useSuperAdminPlans();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm<PostPlanFields>({});

  const tipoPlanSeleccionado = useWatch({ control, name: "id_tipo_plan" });
  const duracion = useWatch({ control, name: "duracion" }) || 0;
  const creditosMes = useWatch({ control, name: "creditos_mensuales" }) || 0;
  const maxUsuarios = useWatch({ control, name: "max_usuarios" }) || 0;
  const [isPrivate, setIsPrivate] = useState(false);
  const { VERSION } = environment;

  useEffect(() => {
    setValue("total_creditos", duracion * creditosMes * maxUsuarios);
  }, [duracion, creditosMes, maxUsuarios, setValue]);

  useEffect(() => {
    const privatePlan = tiposPlan.find(
      (tp) => tp.nombre === PlansTypes.PRIVADO
    );
    if (privatePlan) {
      const { id_tipo_plan } = privatePlan;
      if (id_tipo_plan === tipoPlanSeleccionado) {
        return setIsPrivate(true);
      }
    }
    setValue("id_organizacion", null);
    return setIsPrivate(false);
  }, [tipoPlanSeleccionado]);

  const handleSubmitForm = (data: PostPlanFields) => {
    const newData:PostPlanFields = { ...data, version: VERSION as string };
    console.log(newData);
    planCreate(newData);
    reset();
  };

  const handleClose = () => {
    if (!isCreating) {
      reset();
      toggleCreateModal(false);
    }
  };

  return (
    <Modal
      open={isCreateModalOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.3)",
      }}
    >
      <Box
        component="form"
        sx={{
          width: 900,
          bgcolor: "background.paper",
          borderRadius: 2,
          p: 4,
          boxShadow: 24,
          minWidth: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        {isLoadingTipoPlans ? (
          <Typography
            id="edit-subscription-modal-title"
            justifyContent={"center"}
            sx={{
              width: 1,
              textAlign: "center",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Cargando...
          </Typography>
        ) : (
          <>
            <Typography variant="h6" component="h2">
              Nuevo plan
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Nombre
                </Typography>
                <Controller
                  name="nombre"
                  control={control}
                  rules={{ required: "El nombre es obligatorio" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      margin="normal"
                      error={!!errors.nombre}
                      helperText={errors.nombre?.message}
                      disabled={isCreating}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Descripción
                </Typography>
                <Controller
                  name="descripcion"
                  control={control}
                  rules={{ required: "La descripción es obligatoria" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      margin="normal"
                      error={!!errors.descripcion}
                      helperText={errors.descripcion?.message}
                      disabled={isCreating}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Precio (USD)
                </Typography>
                <Controller
                  name="precio"
                  control={control}
                  rules={{
                    required: "El precio es obligatorio",
                    min: { value: 1, message: "No puede ser 0" },
                    validate: (value) => {
                      if (!Number.isInteger(Number(value))) {
                        return "Debe ser un número entero";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      fullWidth
                      margin="normal"
                      error={!!errors.precio}
                      helperText={errors.precio?.message}
                      disabled={isCreating}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Duración (meses)
                </Typography>
                <Controller
                  name="duracion"
                  control={control}
                  rules={{
                    required: "La duración es obligatoria",
                    min: { value: 1, message: "No puede ser 0" },
                    validate: (value) => {
                      if (!Number.isInteger(Number(value))) {
                        return "Debe ser un número entero";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      fullWidth
                      margin="normal"
                      error={!!errors.duracion}
                      helperText={errors.duracion?.message}
                      disabled={isCreating}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Créditos / mes
                </Typography>
                <Controller
                  name="creditos_mensuales"
                  control={control}
                  rules={{
                    required: "Los créditos por mes son obligatorios",
                    min: { value: 1, message: "No puede ser 0" },
                    validate: (value) => {
                      if (!Number.isInteger(Number(value))) {
                        return "Debe ser un número entero";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      fullWidth
                      margin="normal"
                      error={!!errors.creditos_mensuales}
                      helperText={errors.creditos_mensuales?.message}
                      disabled={isCreating}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Total de créditos
                </Typography>
                <TextField
                  value={duracion * creditosMes * maxUsuarios}
                  fullWidth
                  margin="normal"
                  disabled
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Máximo de Usuarios
                </Typography>
                <Controller
                  name="max_usuarios"
                  control={control}
                  rules={{
                    required: "El máximo de usuarios es obligatorio",
                    min: { value: 1, message: "No puede ser 0" },
                    validate: (value) => {
                      if (!Number.isInteger(Number(value))) {
                        return "Debe ser un número entero";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      fullWidth
                      margin="normal"
                      error={!!errors.max_usuarios}
                      helperText={errors.max_usuarios?.message}
                      disabled={isCreating}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Tipo de Plan
                </Typography>
                <Controller
                  name="id_tipo_plan"
                  control={control}
                  rules={{ required: "El tipo de plan es obligatorio" }}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      margin="normal"
                      error={!!errors.id_tipo_plan}
                    >
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        disabled={isCreating}
                      >
                        {tiposPlan.map((tipo) => (
                          <MenuItem
                            key={tipo.id_tipo_plan}
                            value={tipo.id_tipo_plan}
                          >
                            {tipo.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.id_tipo_plan?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  visibility: isPrivate ? "visible" : "hidden",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Organización
                </Typography>
                <Controller
                  name="id_organizacion"
                  control={control}
                  rules={{
                    required: isPrivate
                      ? "Seleccionar un tipo de plan es obligatorio."
                      : false,
                  }}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      margin="normal"
                      error={!!errors.id_organizacion}
                    >
                      <Select
                        {...field}
                        value={field.value || ""}
                        disabled={isCreating}
                      >
                        {orgs.map((org) => (
                          <MenuItem
                            key={org.id_organizacion}
                            value={org.id_organizacion}
                          >
                            {org.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.id_organizacion?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 3, textAlign: "right" }}>
              <Button
                variant="contained"
                sx={{ background: "#336B87" }}
                disabled={isCreating}
                type="submit"
              >
                Crear
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default CreatePlanModal;
